import { type Attributes, diag } from '@opentelemetry/api'
import Transport from 'winston-transport'

import { isPlainObject, isString, jsonToString } from '../utils/misc'
import type { LoggerOptions } from './'
import { Logger } from './'

export const parseWinstonLog = (
  log: {
    message: string | Attributes
    level: string
  } & Attributes
) => {
  const { level, message, ...attributes } = log
  const bodyMsg = isString(message) ? message : jsonToString(message)

  let meta = attributes

  if (isPlainObject(message)) {
    // FIXME: attributes conflict ??
    meta = {
      ...attributes,
      ...(message as Attributes),
    }
  }

  return {
    level,
    message: bodyMsg,
    meta,
  }
}

export type MXLWinstonOptions = LoggerOptions & {
  apiKey?: string
  maxLevel?: string
  getCustomMeta?: () => Attributes
}

export default class MXLWinston extends Transport {
  private readonly logger: Logger

  private readonly getCustomMeta: (() => Attributes) | undefined

  constructor({
    maxLevel,
    getCustomMeta,
    apiKey,
    ...options
  }: MXLWinstonOptions) {
    diag.debug('Initializing MXL winston transport...')
    super({ level: maxLevel ?? 'info' })
    this.getCustomMeta = getCustomMeta
    this.logger = new Logger({
      ...(apiKey && {
        headers: {
          Authorization: apiKey,
        },
      }),
      ...options,
    })
    diag.debug('MXL winston transport initialized!')
  }

  log(
    info: { message: string | Attributes; level: string } & Attributes,
    callback: () => void
  ) {
    setImmediate(() => {
      this.emit('logged', info)
    })

    diag.debug('Received log from winston')

    const { level, message, meta } = parseWinstonLog(info)
    diag.debug('Sending log to MXL')
    this.logger.postMessage(level, message, {
      ...this.getCustomMeta?.(),
      ...meta,
    })
    diag.debug('Log sent to MXL')

    callback()
  }

  close() {
    diag.debug('Closing MXL winston transport...')
    this.logger
      .shutdown()
      .then(() => {
        diag.debug('MXL winston transport closed!')
        this.emit('finish')
        this.emit('close')
      })
      .catch((err) => {
        console.error('Error closing MXL winston transport:', err)
      })
  }
}
